/*
This file is located in the main branch
Last time the file was updated (UTC): [26.07.2024 9:54:27]
Permissions to update outside the main branch: [false]
*/

import { Form, Modal } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getRoleTypes } from 'axios/roleApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import _ from 'lodash';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { Prop, PropExtended } from 'models/api/prop';
import { RoleItem, RoleTableItem, RoleType } from 'models/api/roles';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { clearTranslations } from 'utils/i18n';
import RoleChangeModalForm from './RoleChangeModalForm';
import { useRoleWorkplaceState } from './Roles';

export type RoleModalFromType = RoleTableItem & { enabled?: boolean };

export type RoleChangeModalFromType = Omit<RoleItem, 'id' | 'roles'> & {
    roles: RoleModalFromType[];
    props: PropExtended[];
};

type RoleChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const initialItem: RoleChangeModalFromType = {
    dsc: '',
    isActive: true,
    isTechnical: false,
    name: '',
    roles: [],
    props: [],
    typeId: 0,
    nameTranslations: {},
    rank: 0,
};

const RoleChangeModal = ({ isOpen = false, toggle }: RoleChangeModalProps) => {
    const api = useApi();
    const { hasRole } = useAuth();
    const [currentActiveTab, setCurrentActiveTab] = useState<string>('1');
    const { selectedItem, handleError, changeItemMutation, addItemMutation } =
        useRoleWorkplaceState();
    const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
    const { t, i18n } = useTranslation();

    const { isFetching } = useQuery(['roleTypes'], () => getRoleTypes(api), {
        onSuccess: (data) => {
            setRoleTypes(data);
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
    });

    const toggleTab = useCallback(
        (tab: string) => {
            setCurrentActiveTab((prev) => (prev !== tab ? tab : prev));
        },
        [setCurrentActiveTab]
    );

    const saveChanges = (data: RoleChangeModalFromType) => {
        const roles = data.roles?.filter((r) => r.enabled).map((i) => i.id);

        const clearProp = (prop: Prop) => ({
            ...prop,
            values: prop.values?.filter((x) => !_.isNil(x.value)) ?? [],
        });

        const item = {
            ...data,
            roles: roles,
            props: data.props.map((i) => clearProp(i)),
        };

        const onSuccess = () => {
            clearTranslations();
            i18n.reloadResources();
        };

        selectedItem
            ? changeItemMutation.mutate(
                  {
                      id: selectedItem.id,
                      ...item,
                  },
                  {
                      onSuccess,
                  }
              )
            : addItemMutation.mutate(item, {
                  onSuccess,
              });

        toggle(false);
    };

    const formHasRole = useCallback(
        (): boolean => (selectedItem ? hasRole('rolesUpdate') : hasRole('rolesCreate')),
        [selectedItem, hasRole]
    );

    const { cultures } = useGeneralSettingsContext();

    const role = useMemo(
        () => ({
            ...initialItem,
            ...selectedItem,
            nameTranslations: cultures.reduce<{ [key: string]: string }>((result, culture) => {
                result[culture.code] = selectedItem
                    ? t('BackOfficeRoleName' + selectedItem.name, {
                          lng: culture.code,
                          defaultValue: '',
                      })
                    : '';
                return result;
            }, {}),
            roles: [],
        }),
        [selectedItem, formHasRole]
    );

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="roles__modal"
            data-testid="role-modal"
        >
            <Form<RoleChangeModalFromType>
                onSubmit={(data) => saveChanges(data)}
                onInvalid={() => setCurrentActiveTab('1')}
                defaultValues={role}
                className="form-in-modal"
            >
                <RoleChangeModalForm
                    currentActiveTab={currentActiveTab}
                    toggle={toggle}
                    toggleTab={toggleTab}
                    roleTypes={roleTypes}
                    formHasRole={formHasRole}
                    roleTypesIsFetching={isFetching}
                />
            </Form>
        </Modal>
    );
};

export default RoleChangeModal;
